import { changedSectionTitles } from './initiativeMarkdown';
import {
  sandboxOntologyOverlayPath,
  sandboxProcessOverlayPath,
} from 'features/sandbox/utils/previewDestination';
import { formatScheduleToHumanReadable } from 'shared/lib/cronUtils';
import { formatDateTime as formatBusinessDateTime, timeValueKind, toInstantDate } from 'shared/time/businessTime';

const TERMINAL_STATUSES = new Set(['succeeded', 'failed', 'cancelled']);
const RESOLVED_CRITERION_STATUSES = new Set([
  'met',
  'waived',
  'not_applicable',
]);
const ACTIVE_WAKE_STATUSES = new Set(['queued', 'claimed']);
const OPEN_SANDBOX_STATUSES = new Set([
  'draft',
  'active',
  'approval_pending',
  'approved',
]);
const TURN_STUCK_MS = 30 * 60_000;
const WAKE_LATE_MS = 5 * 60_000;
const IDLE_MS = 12 * 3_600_000;

const checklistStatusRow = (statuses, kind, key) =>
  statuses?.[`${kind}:${key}`] || statuses?.[key] || {};

export const asArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

export const humanize = (value = '') =>
  String(value || '')
    .replaceAll('.', ' ')
    .replaceAll('_', ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (character) => character.toUpperCase());

export const messageDeliveryLabel = (status) => ({
  queued: 'Sent', claimed: 'Picked up by the agent', consumed: 'Processed',
  cancelled: 'Cancelled', failed: 'Could not be processed',
}[status] || '');

const BLOCKING_SCOPE_LABELS = {
  initiative: 'Initiative',
  sandbox: 'OS Branch',
  completion: 'Completion',
};

/** Plain label for ask blocking scopes; empty when the ask does not block. */
export const humanizeBlockingScope = (scope) => {
  const key = String(scope || 'none');
  if (key === 'none') return '';
  return BLOCKING_SCOPE_LABELS[key] || humanize(key);
};

export const formatDateTime = (value) =>
  timeValueKind(value)
    ? formatBusinessDateTime(value, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';

export const relativeTime = (value, now = Date.now()) => {
  const instant = toInstantDate(value);
  if (!instant) return '';
  const timestamp = instant.getTime();
  if (Number.isNaN(timestamp)) return '';
  const difference = now - timestamp;
  const future = difference < 0;
  const absolute = Math.abs(difference);
  if (absolute < 60_000) return future ? 'in a moment' : 'just now';
  if (absolute < 3_600_000) {
    const minutes = Math.max(1, Math.floor(absolute / 60_000));
    return future ? `in ${minutes}m` : `${minutes}m ago`;
  }
  if (absolute < 86_400_000) {
    const hours = Math.max(1, Math.floor(absolute / 3_600_000));
    return future ? `in ${hours}h` : `${hours}h ago`;
  }
  const days = Math.max(1, Math.floor(absolute / 86_400_000));
  return future ? `in ${days}d` : `${days}d ago`;
};

const timeOf = (value) => {
  const timestamp = new Date(value || 0).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const byOldestFirst = (left, right) => timeOf(left?.created_at) - timeOf(right?.created_at);

const titleCase = (value) =>
  String(value || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.replace(/^./, (character) => character.toUpperCase()))
    .join(' ');

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Profile ID or email address to the name a person would answer to. Asks record
 * recipients as profile IDs, so the directory resolved by the API is consulted
 * first; without a match an ID is dropped, because an ID is not a name.
 */
export const personLabel = (value, directory) => {
  const text = String(value || '').trim();
  if (!text) return '';
  const resolved = directory?.get?.(text.toLowerCase()) || (UUID_RE.test(text) ? '' : text);
  if (!resolved) return '';
  if (!resolved.includes('@')) return resolved;
  const local = resolved.split('@')[0].replace(/[._-]+/g, ' ').trim();
  return titleCase(local) || resolved;
};

/** Lookup of profile ID and email to display name for one Initiative. */
export const buildPeopleDirectory = (people) => {
  const directory = new Map();
  asArray(people).forEach((person) => {
    const name = String(person?.display_name || '').trim();
    if (!name || UUID_RE.test(name)) return;
    [person.user_profile_id, person.email].forEach((key) => {
      const text = String(key || '').trim().toLowerCase();
      if (text) directory.set(text, name);
    });
  });
  return directory;
};

const truncate = (value, limit = 140) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (text.length <= limit) return text;
  return `${text.slice(0, limit - 1)}…`;
};

const activityFacts = (facts) =>
  facts.filter((fact) => {
    if (!fact || fact.value === null || fact.value === undefined) return false;
    return String(fact.value).trim() !== '';
  });

/** "Rewrote Open loops, Timeline +2 more" — long section headings stay readable. */
export const describeChangedSections = (changed = [], max = 3) => {
  const titles = asArray(changed);
  if (!titles.length) return '';
  const named = titles.slice(0, max).map((title) => truncate(title, 28));
  const extra = titles.length - named.length;
  return `Rewrote ${named.join(', ')}${extra > 0 ? ` +${extra} more` : ''}`;
};

const findById = (rows, id) =>
  id ? asArray(rows).find((row) => String(row.id) === String(id)) : null;

const hasNonEmptyAuthority = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.keys(value).some((key) => {
    const entry = value[key];
    if (Array.isArray(entry)) return entry.length > 0;
    if (entry && typeof entry === 'object') return Object.keys(entry).length > 0;
    return Boolean(entry);
  });
};

/** Open asks that require a sponsor decision card in Initiatives. */
export const isDecisionAsk = (ask) =>
  String(ask?.status || '') === 'open' &&
  (String(ask?.response_contract || '') === 'decision' ||
    (String(ask?.blocking_scope || 'none') !== 'none') ||
    hasNonEmptyAuthority(ask?.decide_authority));

const awaitsReply = (ask) =>
  String(ask?.status || '') === 'open' && ask?.expects_response !== false;

/** Who an ask went to, preferring recorded deliveries over authored authority. */
export const askRecipients = (ask, deliveries = []) => {
  const labels = [];
  const push = (value) => {
    const text = String(value || '').trim();
    if (text && !labels.includes(text)) labels.push(text);
  };
  asArray(deliveries)
    .filter((row) => String(row?.ask_id) === String(ask?.id))
    .forEach((row) => {
      const recipient = row?.recipient || {};
      asArray(recipient.emails).forEach(push);
      asArray(recipient.user_profile_ids).forEach(push);
      push(recipient.email);
      push(recipient.user_profile_id);
    });
  if (!labels.length) {
    const authority = ask?.answer_authority || {};
    asArray(authority.emails).forEach(push);
    asArray(authority.user_profile_ids).forEach(push);
  }
  return labels;
};

const recipientSummary = (ask, deliveries, directory) => {
  const people = askRecipients(ask, deliveries)
    .map((recipient) => personLabel(recipient, directory))
    .filter(Boolean);
  if (!people.length) return humanize(ask?.audience || 'worker');
  if (people.length <= 2) return people.join(' and ');
  return `${people[0]}, ${people[1]} +${people.length - 2}`;
};

const askChannel = (ask, deliveries) => {
  if (!ask?.id) return '';
  return asArray(deliveries).find((row) => String(row.ask_id) === String(ask.id))?.channel || '';
};

/**
 * Everyone with an outstanding ask, collapsed into one row per person so the
 * sponsor sees "who owes us something" rather than a list of asks. Recipients
 * recorded only as opaque profile IDs are grouped by audience and counted,
 * because a raw ID is not a name.
 */
export const buildWaitingOnPeople = (openAsks = [], deliveries = [], directory) => {
  const groups = new Map();
  asArray(openAsks)
    .filter(awaitsReply)
    .forEach((ask) => {
      const audience = String(ask.audience || 'worker');
      const recipients = askRecipients(ask, deliveries);
      const keys = recipients.length ? recipients : [`audience:${audience}`];
      keys.forEach((recipient) => {
        const name = personLabel(recipient, directory);
        const groupKey = name || `audience:${audience}`;
        const existing = groups.get(groupKey) || {
          key: groupKey,
          label: name || humanize(audience),
          named: Boolean(name),
          audience,
          people: new Set(),
          askIds: new Set(),
          oldestAt: ask.created_at,
          dueAt: ask.response_due_at || '',
          subjects: [],
        };
        existing.people.add(recipient);
        existing.askIds.add(String(ask.id));
        if (timeOf(ask.created_at) < timeOf(existing.oldestAt)) existing.oldestAt = ask.created_at;
        if (
          ask.response_due_at &&
          (!existing.dueAt || timeOf(ask.response_due_at) < timeOf(existing.dueAt))
        ) {
          existing.dueAt = ask.response_due_at;
        }
        if (ask.subject && !existing.subjects.includes(ask.subject)) {
          existing.subjects.push(ask.subject);
        }
        groups.set(groupKey, existing);
      });
    });
  return [...groups.values()]
    .map(({ people, askIds, ...group }) => {
      const personCount = people.size;
      return {
        ...group,
        personCount,
        askCount: askIds.size,
        label:
          group.named || personCount < 2
            ? group.label
            : `${personCount} ${group.label.toLowerCase()}s`,
      };
    })
    .sort((left, right) => timeOf(left.oldestAt) - timeOf(right.oldestAt));
};

const NOTEBOOK_PATHS = new Set(['initiative.md']);

const isNotebookPath = (path) => {
  const text = String(path || '').trim();
  return NOTEBOOK_PATHS.has(text) || [...NOTEBOOK_PATHS].some((path) => text.endsWith(`/${path}`));
};

/** The Initiative's own working notebook, kept apart from authored deliverables. */
export const notebookDocument = (documents = []) => {
  const notebooks = asArray(documents).filter((doc) => isNotebookPath(doc?.path));
  const populated = notebooks.filter((doc) => String(doc?.content || '').trim());
  const candidates = populated.length ? populated : notebooks;
  return candidates.find((doc) => doc.path === 'initiative.md') || candidates[0] || null;
};

const documentTitle = (doc) => {
  const heading = String(doc?.content || '').match(/^#\s+(.+?)\s*$/m);
  if (heading) return heading[1].trim();
  const name = String(doc?.path || '').split('/').pop() || 'Document';
  return humanize(name.replace(/\.md$/i, ''));
};

const documentSummary = (doc) => {
  const lines = String(doc?.content || '').split(/\r?\n/);
  const prose = lines.find(
    (line) => line.trim() && !line.trim().startsWith('#') && !line.trim().startsWith('|')
  );
  return truncate(prose, 220);
};

/**
 * Documents a reader is meant to read, newest first. The notebook is the
 * Initiative's scratchpad and is deliberately excluded.
 */
export const buildDeliverables = (documents = []) =>
  asArray(documents)
    .filter((doc) => !isNotebookPath(doc?.path))
    .map((doc) => ({
      ...doc,
      title: documentTitle(doc),
      summary: documentSummary(doc),
    }))
    .sort((left, right) => timeOf(right.updated_at) - timeOf(left.updated_at));

const HEADER_LINE_RE = /^(\[[^\]]*\]|[A-Za-z][\w ]{0,24}:\s*\S.{0,60})$/;
const MAX_STRIPPED_HEADER_LINES = 4;

/**
 * Replies arrive with routing headers on top — bracketed markers, `From:` lines,
 * delivery tags. Strip those so the reader sees what the person actually said.
 */
export const cleanMessageBody = (text) => {
  const raw = String(text || '');
  const lines = raw.split(/\r?\n/);
  let index = 0;
  while (
    index < lines.length &&
    index < MAX_STRIPPED_HEADER_LINES &&
    (!lines[index].trim() || HEADER_LINE_RE.test(lines[index].trim()))
  ) {
    index += 1;
  }
  const body = lines.slice(index).join('\n').trim();
  return body || raw.trim();
};

/** One-line preview of a cleaned message. */
export const messagePreview = (text, limit = 200) =>
  truncate(cleanMessageBody(text).replace(/\s+/g, ' '), limit);

const REPORT_AUDIENCE = 'sponsor';

/**
 * The most recent thing the Initiative actually told the operations leader, in the
 * words it used. A bare decision prompt repeats its own subject and carries no
 * findings, so it is not a report.
 */
export const buildLatestReport = (asks = [], responses = [], directory) => {
  const report = asArray(asks)
    .filter((ask) => String(ask?.audience || '') === REPORT_AUDIENCE)
    .filter((ask) => {
      const body = String(ask?.body_text || '').trim();
      return body.length > 0 && body !== String(ask?.subject || '').trim();
    })
    .sort((left, right) => timeOf(right.created_at) - timeOf(left.created_at))[0];
  if (!report) return null;
  const reply = asArray(responses)
    .filter((row) => String(row.ask_id) === String(report.id))
    .sort(byOldestFirst)
    .pop();
  return {
    id: report.id,
    subject: report.subject || 'Latest update',
    body: String(report.body_text).trim(),
    at: report.created_at,
    open: String(report.status) === 'open',
    reply: reply
      ? {
          by:
            personLabel(reply.responder_user_profile_id, directory) ||
            personLabel(reply.from_address, directory) ||
            'Sponsor',
          decision: reply.decision ? humanize(reply.decision) : '',
          at: reply.created_at,
        }
      : null,
  };
};

/**
 * Changes this Initiative made (or proposed) to the live operation, one row per
 * Initiative OS Branch so the reader sees the versioned proposal lineage.
 */
export const buildPublishedWork = (sandboxes = [], sandboxOverlays = []) => {
  const familiesBySandboxId = new Map();
  [...asArray(sandboxOverlays)].sort(byOldestFirst).forEach((overlay) => {
    const summary = overlay.sandbox_summary || overlay.summary || {};
    const byFamily = summary.artifact_summary?.by_family || {};
    const families = Object.keys(byFamily).filter(
      (family) => Number(byFamily[family] || 0) > 0
    );
    if (families.length) {
      familiesBySandboxId.set(String(overlay.sandbox_id || ''), families);
    }
  });
  return asArray(sandboxes)
    .map((sandbox) => {
      const status = String(sandbox.sandbox_status || 'draft');
      return {
        id: `sandbox:${sandbox.id}`,
        title:
          sandbox.title ||
          sandbox.sandbox_name ||
          (status === 'published'
            ? 'Changes published to the live workspace'
            : status === 'approval_pending'
              ? 'Changes prepared, waiting on approval'
              : 'Proposed change'),
        detail: truncate(sandbox.description, 160),
        status,
        sandboxId: sandbox.id || '',
        artifactFamilies: familiesBySandboxId.get(String(sandbox.id || '')) || [],
        at: sandbox.published_at || sandbox.updated_at || sandbox.created_at,
      };
    })
    .sort((left, right) => timeOf(right.at) - timeOf(left.at));
};

/**
 * One row per person rather than one per ask: a reader wants to know who was
 * involved, who still owes an answer, and what they last said.
 */
export const buildPeopleThreads = (
  asks = [],
  deliveries = [],
  responses = [],
  directory,
  now = Date.now()
) => {
  const threads = new Map();
  asArray(asks).forEach((ask) => {
    const audience = String(ask.audience || 'worker');
    const replies = asArray(responses)
      .filter((row) => String(row.ask_id) === String(ask.id))
      .sort(byOldestFirst);
    const recipients = askRecipients(ask, deliveries)
      .map((recipient) => personLabel(recipient, directory))
      .filter(Boolean);
    // An answer with no author on it belongs to the only person who was asked.
    const replyAuthor = (reply) =>
      personLabel(reply?.responder_user_profile_id, directory) ||
      personLabel(reply?.from_address, directory) ||
      (recipients.length === 1 ? recipients[0] : '');
    // Someone who answered is a participant even when the ask was addressed to a
    // role rather than to them by name.
    const participants = [...new Set([...recipients, ...replies.map(replyAuthor)])].filter(Boolean);
    const keys = participants.length ? participants : [`audience:${audience}`];
    keys.forEach((participant) => {
      const name = participants.length ? participant : '';
      const key = name || `audience:${audience}`;
      const ownReplies = name ? replies.filter((reply) => replyAuthor(reply) === name) : replies;
      const latestReply = (ownReplies.length ? ownReplies : replies).slice(-1)[0] || null;
      const thread = threads.get(key) || {
        key,
        name: name || humanize(audience),
        named: Boolean(name),
        audience,
        askCount: 0,
        replyCount: 0,
        openCount: 0,
        dueAt: '',
        lastAt: '',
        lastSubject: '',
        lastReply: '',
        items: [],
      };
      thread.askCount += 1;
      // A thread counts as answered when the exchange got a reply, whoever the
      // reply was recorded against; attribution varies by delivery channel.
      thread.replyCount += replies.length ? 1 : 0;
      if (awaitsReply(ask)) {
        thread.openCount += 1;
        if (
          ask.response_due_at &&
          (!thread.dueAt || timeOf(ask.response_due_at) < timeOf(thread.dueAt))
        ) {
          thread.dueAt = ask.response_due_at;
        }
      }
      const at = latestReply?.created_at || ask.created_at;
      if (timeOf(at) >= timeOf(thread.lastAt)) {
        thread.lastAt = at;
        thread.lastSubject = ask.subject || humanize(ask.purpose) || 'Message';
        thread.lastReply = latestReply
          ? messagePreview(latestReply.body_text || latestReply.objection || latestReply.choice)
          : '';
      }
      const shownReplies = (ownReplies.length ? ownReplies : replies).map((reply) => ({
        id: String(reply.id),
        at: reply.created_at,
        decision: reply.decision ? humanize(reply.decision) : '',
        body: cleanMessageBody(reply.body_text || reply.objection || reply.choice || ''),
      }));
      thread.items.push({
        id: String(ask.id),
        subject: ask.subject || humanize(ask.purpose) || 'Message',
        askBody: String(ask.body_text || '').trim(),
        at: ask.created_at,
        answered: shownReplies.length > 0,
        open: awaitsReply(ask),
        channel: askChannel(ask, deliveries),
        replies: shownReplies,
        // Kept for list-row previews and older callers.
        reply: shownReplies[shownReplies.length - 1]?.body || '',
        replyAt: shownReplies[shownReplies.length - 1]?.at || '',
      });
      threads.set(key, thread);
    });
  });
  return [...threads.values()]
    .map((thread) => ({
      ...thread,
      items: thread.items.sort((left, right) => timeOf(right.at) - timeOf(left.at)),
      overdue: Boolean(thread.dueAt) && timeOf(thread.dueAt) < now,
      status: thread.openCount ? 'waiting' : thread.replyCount ? 'replied' : 'sent',
    }))
    .sort((left, right) => {
      if (Boolean(left.openCount) !== Boolean(right.openCount)) return left.openCount ? -1 : 1;
      return timeOf(right.lastAt) - timeOf(left.lastAt);
    });
};

const ANY_UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * Runtime status strings name internal machinery and carry record IDs. Readers
 * of Initiatives run a plant, not this runtime, so each known condition is
 * restated as a plain sentence and the raw string is kept for the audit trail.
 */
const PLAIN_PROBLEMS = [
  [
    /unconsumed wake deliver/i,
    {
      label: 'Scheduled work has not started',
      detail:
        'The agent has work queued that it has not picked up. This usually clears itself; if it does not, restart the Agent.',
    },
  ],
  [
    /completion approval invalid/i,
    {
      label: 'Completion could not be signed off',
      detail:
        'The agent reported the initiative finished, but the sign-off did not validate. Read the final update and approve it again.',
    },
  ],
  [
    /worker|turn/i,
    {
      label: 'A work cycle did not finish',
      detail: 'The agent started working and stopped before saving. It will retry on the next cycle.',
    },
  ],
];

export const plainProblem = (raw) => {
  const text = String(raw || '').trim();
  if (!text) return null;
  if (/llm_output_exhausted|response limit before finishing/i.test(text)) {
    return {
      label: 'The agent reached its response limit',
      detail: 'The last attempt stopped before it could finish.',
      technical: text,
    };
  }
  const match = PLAIN_PROBLEMS.find(([pattern]) => pattern.test(text));
  if (match) return { ...match[1], technical: text };
  return {
    label: humanize(text.replace(ANY_UUID_RE, '').replace(/[:\s]+$/, '')),
    detail: '',
    technical: text,
  };
};

const queuedRetry = (item, now) => {
  if (item?.status !== 'queued' || !(Number(item?.attempt_count) > 0) ||
      !timeOf(item?.available_at) ||
      !['turn_failed', 'llm_output_exhausted', 'llm_response_start_timeout',
        'ai_rate_limited', 'ai_capacity_limited', 'ai_budget_unavailable'].includes(item?.status_reason)) return null;
  const waiting = timeOf(item.available_at) > now;
  return {
    label: waiting ? 'Retry scheduled' : 'Retry queued',
    reason: `${item.status_reason === 'llm_output_exhausted' ? 'The agent reached its response limit.' : 'The last attempt failed.'} ${
      waiting ? `Retry scheduled for ${formatDateTime(item.available_at)}.` : 'The retry is ready to start.'}`,
  };
};

/**
 * Initiative activity rebuilt from durable control-plane rows. Initiatives do not
 * publish an event stream, so notebook revisions, asks, replies, sandboxes,
 * and publish runs are the record of what happened.
 */
export const buildInitiativeTimeline = (data, directory = buildPeopleDirectory(data?.initiative_people)) => {
  const documents = asArray(data?.agent_documents);
  const documentById = new Map(documents.map((doc) => [String(doc.id), doc]));
  const asks = asArray(data?.initiative_asks);
  const deliveries = asArray(data?.initiative_ask_deliveries);
  const sandboxes = asArray(data?.sandboxes);
  const sandboxById = new Map(sandboxes.map((row) => [String(row.id), row]));
  const sandboxOverlays = asArray(data?.sandbox_overlays);
  const publishRuns = asArray(data?.sandbox_publish_runs);
  const publishedSandboxIds = new Set(
    publishRuns
      .filter((run) => String(run.status) === 'completed')
      .map((run) => String(run.workspace_sandbox_id || ''))
      .filter(Boolean)
  );
  const entries = [];

  asArray(data?.initiative_configuration_revisions).forEach((revision) => {
    entries.push({
      id: `agent-configuration:${revision.id}`,
      at: revision.created_at,
      category: 'change',
      actor: revision.created_by_user_profile_id ? 'Sponsor' : 'System',
      title:
        Number(revision.revision || 1) === 1
          ? 'Created initiative configuration'
          : `Updated Agent configuration to revision ${revision.revision}`,
      detail: String(revision.change_summary || '').trim(),
      facts: activityFacts([
        { label: 'Revision', value: revision.revision ? `r${revision.revision}` : '' },
        { label: 'Initiative name', value: revision.title },
      ]),
    });
  });

  asArray(data?.initiative_wake_rules).forEach((rule) => {
    entries.push({
      id: `agent-wake-rule:${rule.id}`,
      at: rule.updated_at || rule.created_at,
      category: 'change',
      actor: rule.updated_by_user_profile_id || rule.created_by_user_profile_id ? 'Sponsor' : 'System',
      title: `${rule.is_active ? 'Configured' : 'Disabled'} ${humanize(rule.kind)} wake: ${rule.name}`,
      detail:
        rule.kind === 'schedule'
          ? rule.schedule_type === 'once'
            ? `One-time wake at ${formatDateTime(rule.run_at)}`
            : formatScheduleToHumanReadable(rule.cron_expression, rule.timezone)
          : 'Published Signal wake',
      facts: activityFacts([
        { label: 'Kind', value: humanize(rule.kind) },
        { label: 'Status', value: rule.is_active ? 'Enabled' : 'Disabled' },
        { label: 'Revision', value: rule.revision ? `r${rule.revision}` : '' },
      ]),
    });
  });

  const previousRevision = new Map();
  [...asArray(data?.agent_document_revisions)].sort(byOldestFirst).forEach((revision) => {
    const documentKey = String(revision.document_id);
    const document = documentById.get(documentKey) || {};
    const path = document.path || 'Initiative document';
    const notebook = isNotebookPath(path);
    const previous = previousRevision.get(documentKey);
    previousRevision.set(documentKey, revision);
    const changed = previous ? changedSectionTitles(previous.content, revision.content) : [];
    entries.push({
      id: `revision:${revision.id}`,
      at: revision.created_at,
      category: notebook ? 'notebook' : 'artifact',
      actor: revision.created_by_actor_kind === 'user' ? 'Sponsor' : 'Agent',
      title: notebook
        ? previous
          ? `Updated ${path} to r${revision.revision}`
          : `Started ${path}`
        : previous
          ? `Updated ${documentTitle(document)} to r${revision.revision}`
          : `Built ${documentTitle(document)}`,
      detail: describeChangedSections(changed, 2),
      turnId: revision.turn_id || null,
      related: notebook
        ? []
        : [
            {
              kind: 'document',
              label: 'Open artifact',
              documentPath: path,
            },
          ],
      facts: activityFacts([
        { label: 'Document', value: path },
        { label: 'Revision', value: revision.revision ? `r${revision.revision}` : '' },
        { label: 'Sections changed', value: changed.join(', ') },
      ]),
    });
  });

  asks.forEach((ask) => {
    const recipient = recipientSummary(ask, deliveries, directory);
    const channel = askChannel(ask, deliveries);
    const linkedSandbox = sandboxById.get(
      String(ask.decision_payload?.sandbox_id || ask.sandbox_id || '')
    );
    const showSandboxFact =
      String(ask.decision_kind || '') === 'sandbox_publication' ||
      String(ask.blocking_scope || '') === 'sandbox';
    entries.push({
      id: `ask:${ask.id}`,
      at: ask.created_at,
      category: isDecisionAsk(ask) || String(ask.response_contract) === 'decision' ? 'decision' : 'ask',
      actor: 'Agent',
      title: `Asked ${recipient}: ${ask.subject || humanize(ask.purpose)}`,
      detail: String(ask.body_text || '').trim(),
      turnId: ask.turn_id || null,
      askId: String(ask.id),
      related: [],
      facts: activityFacts([
        { label: 'Recipient', value: recipient },
        { label: 'Channel', value: channel ? humanize(channel) : '' },
        { label: 'Status', value: humanize(ask.status) },
        { label: 'Purpose', value: humanize(ask.purpose) },
        {
          label: 'Response',
          value: ask.expects_response === false ? 'Not expected' : 'Expected',
        },
        { label: 'Response format', value: humanize(ask.response_contract) },
        { label: 'Due', value: formatDateTime(ask.response_due_at) },
        {
          label: 'Blocking scope',
          value: humanizeBlockingScope(ask.blocking_scope),
        },
        {
          label: 'OS Branch',
          value: showSandboxFact
            ? linkedSandbox?.title || linkedSandbox?.sandbox_name || ''
            : '',
        },
      ]),
    });
    if (['expired', 'cancelled'].includes(String(ask.status)) && ask.resolved_at) {
      entries.push({
        id: `ask-closed:${ask.id}`,
        at: ask.resolved_at,
        category: 'risk',
        actor: 'System',
        title: `Ask ${ask.status} without a reply: ${ask.subject || humanize(ask.purpose)}`,
        detail: String(ask.body_text || '').trim(),
        facts: activityFacts([
          { label: 'Status', value: humanize(ask.status) },
          { label: 'Recipient', value: recipient },
          { label: 'Resolved', value: formatDateTime(ask.resolved_at) },
        ]),
      });
    }
  });

  asArray(data?.initiative_ask_responses).forEach((response) => {
    const ask = findById(asks, response.ask_id);
    const authorizedAnswerers =
      response.kind === 'answer'
        ? askRecipients(ask, deliveries)
            .map((recipient) => personLabel(recipient, directory))
            .filter(Boolean)
        : [];
    const who =
      personLabel(response.from_address, directory) ||
      (authorizedAnswerers.length === 1 ? authorizedAnswerers[0] : '') ||
      personLabel(response.responder_user_profile_id, directory) ||
      (String(ask?.audience || '') === 'sponsor' ? 'Sponsor' : 'A teammate');
    const decision = response.decision ? humanize(response.decision) : '';
    entries.push({
      id: `response:${response.id}`,
      at: response.created_at,
      category: response.kind === 'decision' ? 'decision' : 'reply',
      actor: who,
      title: decision
        ? `${who} ${decision.toLowerCase()}: ${ask?.subject || 'a sponsor decision'}`
        : `${who} replied: ${ask?.subject || 'an open ask'}`,
      detail: String(response.body_text || response.objection || response.choice || '').trim(),
      askId: String(response.ask_id || ''),
      responseId: String(response.id),
      related: [],
      facts: activityFacts([
        { label: 'Related request', value: ask?.subject || humanize(ask?.purpose) },
        { label: 'Response type', value: humanize(response.kind) },
        { label: 'Decision', value: decision },
        { label: 'Channel', value: humanize(response.channel) },
        { label: 'Request status', value: humanize(ask?.status) },
      ]),
    });
  });

  sandboxes.forEach((sandbox) => {
    const status = String(sandbox.sandbox_status || 'draft');
    const title =
      sandbox.title || sandbox.sandbox_name || 'OS Branch changes';
    entries.push({
      id: `sandbox:${sandbox.id}`,
      at: sandbox.created_at,
      category: 'change',
      actor: 'Agent',
      title: `Opened governed OS Branch: ${title}`,
      detail: String(sandbox.description || '').trim(),
      sessionId: sandbox.source_session_id || null,
      related: [
        {
          kind: 'sandbox',
          label: 'Open artifact',
          sandboxId: String(sandbox.id),
        },
      ],
      facts: activityFacts([
        { label: 'Status', value: humanize(status) },
        { label: 'Risk', value: humanize(sandbox.risk_level) },
        { label: 'Effect', value: humanize(sandbox.effect_kind) },
      ]),
    });
    if (status === 'approval_pending' && sandbox.updated_at) {
      entries.push({
        id: `sandbox-pending:${sandbox.id}`,
        at: sandbox.updated_at,
        category: 'decision',
        actor: 'Agent',
        title: 'Prepared changes for publication',
        detail:
          String(sandbox.description || '').trim() ||
          'The exact OS Revision is waiting for review.',
        sessionId: sandbox.source_session_id || null,
        related: [
          {
            kind: 'sandbox',
            label: 'Review changes',
            sandboxId: String(sandbox.id),
          },
        ],
        facts: activityFacts([
          { label: 'OS Branch', value: title },
          { label: 'Status', value: 'Waiting for approval' },
          { label: 'Risk', value: humanize(sandbox.risk_level) },
        ]),
      });
    }
    if (
      !OPEN_SANDBOX_STATUSES.has(status) &&
      status !== 'published' &&
      sandbox.updated_at
    ) {
      entries.push({
        id: `sandbox-status:${sandbox.id}`,
        at: sandbox.updated_at,
        category: 'change',
        actor: 'System',
        title: `${humanize(status)}: ${title}`,
        detail: String(sandbox.description || '').trim(),
        sessionId: sandbox.source_session_id || null,
        related: [
          {
            kind: 'sandbox',
            label: 'Open artifact',
            sandboxId: String(sandbox.id),
          },
        ],
        facts: activityFacts([
          { label: 'Status', value: humanize(status) },
          { label: 'Risk', value: humanize(sandbox.risk_level) },
          { label: 'Effect', value: humanize(sandbox.effect_kind) },
        ]),
      });
    }
    if (
      status === 'published' &&
      !publishedSandboxIds.has(String(sandbox.id)) &&
      (sandbox.published_at || sandbox.updated_at)
    ) {
      entries.push({
        id: `sandbox-status:${sandbox.id}`,
        at: sandbox.published_at || sandbox.updated_at,
        category: 'publish',
        actor: 'System',
        title: 'OS Branch changes were published',
        detail: String(sandbox.description || '').trim(),
        sessionId: sandbox.source_session_id || null,
        related: [
          {
            kind: 'sandbox',
            label: 'Open published artifact',
            sandboxId: String(sandbox.id),
          },
        ],
        facts: activityFacts([
          { label: 'OS Branch', value: title },
          { label: 'Status', value: 'Published' },
          { label: 'Published', value: formatDateTime(sandbox.published_at) },
        ]),
      });
    }
  });

  const previousFamiliesBySandbox = new Map();
  [...sandboxOverlays].sort(byOldestFirst).forEach((overlay) => {
    const summary = overlay.sandbox_summary || {};
    const artifactSummary = summary.artifact_summary || {};
    const byFamily = artifactSummary.by_family || {};
    const families = Object.keys(byFamily).filter((family) => Number(byFamily[family] || 0) > 0);
    if (!families.length) return;
    const sandboxId = String(overlay.sandbox_id || '');
    const previousFamilies = previousFamiliesBySandbox.get(sandboxId) || new Set();
    const addedFamilies = families.filter((family) => !previousFamilies.has(family));
    previousFamiliesBySandbox.set(sandboxId, new Set(families));
    const addedOntology = addedFamilies.includes('ontology_schema_delta');
    const addedProcess = addedFamilies.includes('operational_process_version');
    const sourceLinkedCount = Number(summary.object_upsert_count || 0);
    const relationshipCount = Number(summary.relationship_upsert_count || 0);
    let title = `Updated ${families.map(humanize).join(' and ')}`;
    if (addedOntology && addedProcess) title = 'Built ontology model and current process map';
    else if (addedOntology) title = 'Built ontology model';
    else if (addedProcess) title = 'Added current process map';
    else if (sourceLinkedCount || relationshipCount) title = 'Added source-linked operating records';
    const sandbox = sandboxById.get(sandboxId);
    const related = [];
    if (sandboxId && addedProcess) {
      related.push({
        kind: 'process_map',
        label: 'Open process map',
        href: sandboxProcessOverlayPath(sandboxId),
      });
    }
    if (sandboxId && families.includes('ontology_schema_delta')) {
      related.push({
        kind: 'ontology_graph',
        label: 'Open ontology changes',
        href: sandboxOntologyOverlayPath(sandboxId),
      });
    }
    if (sandboxId && families.includes('operational_process_version') && !addedProcess) {
      related.push({
        kind: 'process_map',
        label: 'Open process map',
        href: sandboxProcessOverlayPath(sandboxId),
      });
    }
    entries.push({
      id: `sandbox-overlay:${overlay.id}`,
      at: overlay.created_at || overlay.updated_at,
      category: 'artifact',
      actor: 'Agent',
      title,
      detail: String(sandbox?.description || '').trim(),
      sessionId: sandbox?.source_session_id || null,
      related,
      facts: activityFacts([
        {
          label: 'Artifact families',
          value: families.map(humanize).join(', '),
        },
        {
          label: 'OS Branch revision',
          value: overlay.sandbox_revision_number
            ? `r${overlay.sandbox_revision_number}`
            : '',
        },
        {
          label: 'Source-linked records',
          value: sourceLinkedCount ? String(sourceLinkedCount) : '',
        },
        {
          label: 'Relationships',
          value: relationshipCount ? String(relationshipCount) : '',
        },
      ]),
    });
  });

  publishRuns.forEach((run) => {
    if (String(run.status) !== 'completed') return;
    const sandbox = sandboxById.get(String(run.workspace_sandbox_id || ''));
    entries.push({
      id: `publish-run:${run.id}`,
      at: run.completed_at || run.created_at || run.started_at,
      category: 'publish',
      actor: 'System',
      title: 'OS Branch changes were published',
      detail: String(sandbox?.description || '').trim(),
      sessionId: run.chat_session_id || sandbox?.source_session_id || null,
      related: sandbox?.id
        ? [
            {
              kind: 'sandbox',
              label: 'Open published artifact',
              sandboxId: String(sandbox.id),
            },
          ]
        : [],
      facts: activityFacts([
        {
          label: 'OS Branch',
          value: sandbox?.title || sandbox?.sandbox_name,
        },
        { label: 'Status', value: 'Published' },
        { label: 'Published', value: formatDateTime(run.completed_at) },
      ]),
    });
  });

  return entries
    .filter((entry) => entry.at)
    .sort((left, right) => timeOf(right.at) - timeOf(left.at));
};

const decisionAsksFromItem = (item) =>
  asArray(item?.initiative_asks).filter(isDecisionAsk);

export const initiativeBucket = (item) => {
  if (item?.status === 'draft') return 'draft';
  if (item?.status === 'paused') return 'paused';
  if (['succeeded', 'cancelled'].includes(item?.status)) return 'done';
  const pendingDecisions = decisionAsksFromItem(item);
  if (pendingDecisions.length || item?.status === 'approval_required') return 'action';
  if (['needs_attention', 'failed'].includes(item?.status)) return 'risk';
  if (item?.status === 'waiting') return 'waiting';
  return 'active';
};

export const initiativeListSummary = (item, now = Date.now()) => {
  const initiative = asArray(item?.initiatives)[0] || item?.initiatives || item || {};
  const statuses = Object.values(initiative.criterion_statuses || item?.criterion_statuses || {});
  const resolved = statuses.filter((row) =>
    RESOLVED_CRITERION_STATUSES.has(row?.status)
  ).length;
  const bucket = initiativeBucket(item);
  const pendingDecisions = decisionAsksFromItem(item);
  const openAsks = asArray(item?.initiative_asks).filter((row) => row.status === 'open');
  const awaitingReply = openAsks.filter(awaitsReply);
  const labels = {
    action: 'Needs decision',
    risk: item?.status === 'failed' ? 'Failed' : 'Needs attention',
    waiting: 'Waiting',
    done: item?.status === 'succeeded' ? 'Completed' : humanize(item?.status),
    draft: 'Ready to start',
    paused: 'Paused',
    active: item?.status === 'queued' ? 'Queued' : 'In progress',
  };
  const owner =
    asArray(item?.agents)[0]?.name ||
    item?.agents?.name ||
    null;
  const directive = String(initiative.directive_markdown || item?.directive_markdown || '').trim();
  const working = ['running', 'queued'].includes(item?.status);
  const retry = queuedRetry(item, now);
  return {
    initiative,
    ui: {
      avatar_key: 'operator',
      accent: 'cyan',
      ...(item?.spec?.ui || {}),
      ...(initiative?.spec?.ui || {}),
      ...(item?.ui || {}),
      ...(initiative?.ui || {}),
    },
    bucket,
    label: retry?.label || labels[bucket],
    resolved,
    pendingDecisionCount: pendingDecisions.length,
    openAskCount: awaitingReply.length,
    ownerName: owner,
    snippet: directive,
    reason:
      (item?.status === 'draft' ? 'Not started' : item?.status === 'paused' ? 'Resume to continue' : '') ||
      pendingDecisions[0]?.subject ||
      retry?.reason ||
      (working ? (item.status === 'running' ? 'Working now' : 'Waiting to start') : '') ||
      (item?.status_reason ? humanize(item.status_reason) : '') ||
      awaitingReply[0]?.subject ||
      // The directive is the agent's instructions (tool names and all); a card
      // only says, in owner terms, what the work is doing.
      (bucket === 'waiting' ? 'Waiting until its next check' : '') ||
      '',
    updatedLabel: relativeTime(item?.updated_at, now),
  };
};

export const waitingPeopleCount = (waitingOnPeople = []) =>
  asArray(waitingOnPeople).reduce((total, person) => total + (person.personCount || 1), 0);

const responsibilityFor = ({
  status,
  pendingDecisionAsks,
  waitingOnPeople,
  pendingWaitSets,
  nextWakeAt,
}) => {
  if (pendingDecisionAsks.length) {
    return {
      key: 'sponsor',
      label: 'You',
      detail: `${pendingDecisionAsks.length} sponsor decision${
        pendingDecisionAsks.length === 1 ? '' : 's'
      } pending`,
    };
  }
  if (status === 'running') {
    return { key: 'agent', label: 'Agent', detail: 'A work cycle is running now' };
  }
  if (status === 'queued') {
    return { key: 'agent', label: 'Agent', detail: 'The next work cycle is queued' };
  }
  if (TERMINAL_STATUSES.has(status)) {
    return { key: 'none', label: 'Nobody', detail: 'The initiative is closed' };
  }
  if (status === 'paused') {
    return { key: 'sponsor', label: 'You', detail: 'Resume the initiative to continue' };
  }
  if (status === 'draft') {
    return { key: 'sponsor', label: 'You', detail: 'Start the initiative when ready' };
  }
  if (waitingOnPeople.length && waitingOnPeople.every((person) => person.audience === 'sponsor')) {
    return {
      key: 'sponsor',
      label: 'You',
      detail: 'The agent is waiting on your answer',
    };
  }
  if (waitingOnPeople.length) {
    const named = waitingOnPeople.slice(0, 2).map((person) => person.label).join(', ');
    const extra = waitingOnPeople.length > 2 ? ` +${waitingOnPeople.length - 2}` : '';
    const total = waitingPeopleCount(waitingOnPeople);
    return {
      key: 'people',
      label: `${named}${extra}`,
      detail: `${total} ${total === 1 ? 'person owes' : 'people owe'} Agent a reply`,
    };
  }
  if (pendingWaitSets.length) {
    return {
      key: 'clock',
      label: nextWakeAt ? formatDateTime(nextWakeAt) : 'A wait condition',
      detail: `${pendingWaitSets.length} wait${
        pendingWaitSets.length === 1 ? '' : 's'
      } must be satisfied first`,
    };
  }
  return { key: 'agent', label: 'Agent', detail: 'Working from the saved initiative state' };
};

const stallFor = ({ status, now, updatedAt, wakeDeliveries, lastUpdateAt, futureDueAt }) => {
  if (TERMINAL_STATUSES.has(status) || status === 'draft' || status === 'paused') return null;
  if (status === 'running' && now - timeOf(updatedAt) > TURN_STUCK_MS) {
    return {
      key: 'turn_stuck',
      label: 'A work cycle did not finish',
      detail: 'The agent has been working for a long time without saving anything.',
      at: updatedAt,
    };
  }
  // Pending deliveries may be waiting behind this active cycle, and claimed
  // deliveries remain active until it ends. Neither means work has not started.
  if (status === 'running') return null;
  const lateWake = wakeDeliveries
    .filter((row) => ACTIVE_WAKE_STATUSES.has(String(row.status)))
    .find((row) => row.available_at && now - timeOf(row.available_at) > WAKE_LATE_MS);
  if (lateWake) {
    return {
      key: 'wake_late',
      label: 'Scheduled work has not started',
      detail: 'The agent was due to pick this up and has not.',
      at: lateWake.available_at,
    };
  }
  if (futureDueAt) return null;
  if (lastUpdateAt && now - timeOf(lastUpdateAt) > IDLE_MS) {
    return {
      key: 'idle',
      label: 'No progress recently',
      detail: 'Nothing has changed and nothing is scheduled.',
      at: lastUpdateAt,
    };
  }
  return null;
};

export const deriveInitiativePresentation = (data, now = Date.now()) => {
  const initiative = asArray(data?.initiatives)[0] || data?.initiatives || {};
  const content =
    data?.initiative_content || data?.current_charter || {};
  const asks = asArray(data?.initiative_asks);
  const askResponses = asArray(data?.initiative_ask_responses);
  const askDeliveries = asArray(data?.initiative_ask_deliveries);
  const openAsks = asks.filter((row) => row.status === 'open');
  const pendingDecisionAsks = openAsks.filter(isDecisionAsk);
  const initiativeBlockingAsks = pendingDecisionAsks.filter(
    (row) => (row.blocking_scope || 'none') === 'initiative'
  );
  const scopedPendingAsks = pendingDecisionAsks.filter(
    (row) => (row.blocking_scope || 'none') !== 'initiative'
  );
  const waitSets = asArray(data?.initiative_wait_sets);
  const waitConditions = asArray(data?.initiative_wait_conditions);
  const wakeDeliveries = asArray(data?.initiative_wake_deliveries);
  const sandboxes = asArray(data?.sandboxes);
  const sandboxOverlays = asArray(data?.sandbox_overlays);
  const sandboxPublishRuns = asArray(data?.sandbox_publish_runs);
  const documents = asArray(data?.agent_documents);
  const documentRevisions = asArray(data?.agent_document_revisions);
  const pendingWaitSets = waitSets.filter((row) => row.status === 'pending');
  const waitingOpen =
    openAsks.find((row) => row.expects_response !== false) ||
    pendingWaitSets[0] ||
    null;
  const subscriptions = asArray(data?.signal_subscriptions).filter(
    (row) => row.is_active !== false
  );
  const people = buildPeopleDirectory(data?.initiative_people);
  const nameFor = (value) => personLabel(value, people);
  const timeline = buildInitiativeTimeline(data, people);
  const lastUpdate = timeline[0] || null;
  const waitingOnPeople = buildWaitingOnPeople(openAsks, askDeliveries, people);
  const mapChecklist = (kind, items) =>
    asArray(items).map((item) => {
      const row = checklistStatusRow(initiative.criterion_statuses, kind, item.key);
      return {
        ...item,
        kind,
        status: row.status || 'unknown',
        summary: row.summary || row.note || '',
        note: row.summary || row.note || '',
        updatedAt: row.updated_at || '',
      };
    });
  const criteria = mapChecklist('success', content.success_criteria);
  const guardrails = mapChecklist('guardrail', content.guardrails);
  const checklistItems = [...criteria, ...guardrails];
  const resolvedCriteria = criteria.filter((criterion) =>
    RESOLVED_CRITERION_STATUSES.has(criterion.status)
  );
  const resolvedGuardrails = guardrails.filter((item) =>
    RESOLVED_CRITERION_STATUSES.has(item.status)
  );
  const resolvedChecklist = checklistItems.filter((item) =>
    RESOLVED_CRITERION_STATUSES.has(item.status)
  );
  const dueDates = [
    ...openAsks.map((row) => row.response_due_at || row.expires_at),
    ...waitConditions
      .filter((row) => row.status === 'pending' && row.kind === 'timer')
      .map((row) => row.available_at),
    ...wakeDeliveries
      .filter((row) => ACTIVE_WAKE_STATUSES.has(String(row.status)))
      .map((row) => row.available_at),
  ]
    .filter(Boolean)
    .sort((left, right) => timeOf(left) - timeOf(right));
  const nextWakeAt = data?.available_at || dueDates[0] || '';
  const overdue = dueDates.some((value) => timeOf(value) < now);
  const status = data?.status || 'draft';
  const responsibility = responsibilityFor({
    status,
    pendingDecisionAsks,
    waitingOnPeople,
    pendingWaitSets,
    nextWakeAt,
  });
  const stall = stallFor({
    status,
    now,
    updatedAt: data?.updated_at,
    wakeDeliveries,
    lastUpdateAt: lastUpdate?.at,
    futureDueAt: dueDates.find((value) => timeOf(value) > now) || '',
  });
  const problem =
    status === 'needs_attention' || status === 'failed'
      ? plainProblem(data?.last_error || data?.status_reason)
      : null;
  const report = buildLatestReport(asks, askResponses, people);
  const deliverables = buildDeliverables(documents);
  const notebook = notebookDocument(documents);
  const publishedWork = buildPublishedWork(sandboxes, sandboxOverlays);
  const peopleThreads = buildPeopleThreads(asks, askDeliveries, askResponses, people, now);
  const evidence = {
    notebookRevisions: documentRevisions.length,
    documents: documents.length,
    asksOut: asks.length,
    repliesIn: askResponses.length,
    openAsks: openAsks.length,
    sandboxes: sandboxes.length,
    publishedSandboxes: sandboxes.filter(
      (row) => String(row.sandbox_status) === 'published'
    ).length,
    openSandboxes: sandboxes.filter((row) =>
      OPEN_SANDBOX_STATUSES.has(String(row.sandbox_status || ''))
    ).length,
  };

  let state = {
    key: status,
    label: humanize(status),
    headline: data?.status_reason ? humanize(data.status_reason) : 'Initiative status is available',
    description: '',
    priority: 4,
    tone: 'slate',
  };
  if ((pendingDecisionAsks.length && !['draft', 'paused', ...TERMINAL_STATUSES].includes(status)) || status === 'approval_required') {
    const globallyBlocked = initiativeBlockingAsks.length > 0;
    state = {
      key: 'action',
      label: 'Needs your decision',
      headline: pendingDecisionAsks[0]?.subject || 'The agent needs your response',
      description: globallyBlocked
        ? 'Reply to the request in the conversation so the agent can continue.'
        : 'Review the request in the conversation. The agent can continue other work while you decide.',
      priority: 0,
      tone: 'violet',
    };
  } else if (status === 'needs_attention' || status === 'failed') {
    state = {
      key: 'risk',
      label: status === 'failed' ? 'Failed' : 'Needs attention',
      headline: problem?.label || 'The agent hit a problem that needs review',
      description:
        problem?.detail ||
        (status === 'failed' ? 'The agent stopped before completing its outcomes.' : ''),
      priority: 1,
      tone: 'rose',
    };
  } else if (status === 'waiting') {
    const channel = askChannel(waitingOpen, askDeliveries);
    state = {
      key: overdue ? 'risk' : 'waiting',
      label: overdue ? 'Response overdue' : channel === 'email' ? 'Waiting for reply' : 'Waiting',
      headline:
        waitingOpen?.subject ||
        waitingOpen?.purpose ||
        waitingOpen?.context_note ||
        subscriptions[0]?.context_note ||
        humanize(data?.status_reason) ||
        'Waiting for new information',
      description: channel
        ? `Agent will resume when the ${humanize(channel).toLowerCase()} response arrives.`
        : 'The agent has saved its notebook and will resume when a pending wait is satisfied.',
      priority: overdue ? 1 : 2,
      tone: overdue ? 'rose' : 'amber',
    };
  } else if (status === 'running' || status === 'queued') {
    const retry = queuedRetry(data, now);
    state = {
      key: 'active',
      label: retry?.label || (status === 'running' ? 'Working now' : 'Queued'),
      // Never headline an open ask here: an outstanding question is not the
      // work in flight, and reading it as progress hides a parked Initiative.
      headline: retry?.reason || (status === 'queued' ? 'Waiting to start' : lastUpdate?.title || 'Working on the task'),
      description:
        retry ? 'Previously saved work is still available.' : status === 'queued'
          ? 'The next work cycle is ready to start.'
          : 'Updates, results, and questions will appear in the conversation.',
      priority: 3,
      tone: 'cyan',
    };
  } else if (status === 'succeeded') {
    state = {
      key: 'done',
      label: 'Completed',
      headline: 'All required outcomes are confirmed',
      description: 'No further work is required.',
      priority: 5,
      tone: 'emerald',
    };
  } else if (status === 'paused') {
    state = {
      key: 'paused',
      label: 'Paused',
      headline: humanize(data?.status_reason) || 'Work is paused',
      description: 'Resume the initiative to continue.',
      priority: 4,
      tone: 'slate',
    };
  } else if (status === 'draft') {
    state = {
      key: 'draft',
      label: 'Ready to start',
      headline: 'Review the directive and start when ready',
      description: 'Nobody has started work on this initiative yet.',
      priority: 4,
      tone: 'blue',
    };
  } else if (status === 'cancelled') {
    state = {
      key: 'cancelled',
      label: 'Cancelled',
      headline: humanize(data?.status_reason) || 'Initiative was cancelled',
      description: 'No further work will run.',
      priority: 5,
      tone: 'slate',
    };
  }

  return {
    initiative,
    content,
    status,
    state,
    criteria,
    guardrails,
    checklistItems,
    resolvedCriteria,
    resolvedGuardrails,
    resolvedChecklist,
    asks,
    askResponses,
    askDeliveries,
    openAsks,
    pendingDecisionAsks,
    initiativeBlockingAsks,
    scopedPendingAsks,
    waitSets,
    pendingWaitSets,
    waitConditions,
    wakeDeliveries,
    sandboxes,
    sandboxPublishRuns,
    documents,
    documentRevisions,
    waitingOpen,
    waitingOnPeople,
    nameFor,
    subscriptions,
    timeline,
    lastUpdate,
    responsibility,
    stall,
    problem,
    report,
    deliverables,
    notebook,
    publishedWork,
    peopleThreads,
    configurationRevisions: asArray(data?.initiative_configuration_revisions),
    wakeRules: asArray(data?.initiative_wake_rules),
    evidence,
    nextWakeAt,
    overdue,
    progressLabel: checklistItems.length
      ? `${resolvedChecklist.length} of ${checklistItems.length} checklist items resolved`
      : 'No checklist items defined',
    criteriaProgressLabel: criteria.length
      ? `${resolvedCriteria.length} of ${criteria.length} confirmed`
      : 'None defined',
    guardrailProgressLabel: guardrails.length
      ? `${resolvedGuardrails.length} of ${guardrails.length} cleared`
      : 'None defined',
    workProgressLabel: [
      `${evidence.notebookRevisions} notebook update${evidence.notebookRevisions === 1 ? '' : 's'}`,
      `${evidence.asksOut} ask${evidence.asksOut === 1 ? '' : 's'} out`,
      `${evidence.repliesIn} repl${evidence.repliesIn === 1 ? 'y' : 'ies'} in`,
    ].join(' · '),
  };
};

export const sortInitiativesForOperations = (items, now = Date.now()) =>
  [...asArray(items)].sort((left, right) => {
    const leftSummary = initiativeListSummary(left, now);
    const rightSummary = initiativeListSummary(right, now);
    const priority = { action: 0, risk: 1, waiting: 2, active: 3, paused: 4, draft: 5, done: 6 };
    const priorityDifference = priority[leftSummary.bucket] - priority[rightSummary.bucket];
    if (priorityDifference) return priorityDifference;
    return timeOf(right.updated_at) - timeOf(left.updated_at);
  });

// Queue buckets, most urgent first.
export const BUCKET_TONES = {
  action: 'violet',
  risk: 'rose',
  waiting: 'amber',
  active: 'cyan',
  paused: 'slate',
  draft: 'blue',
  done: 'emerald',
};

export const BUCKET_ORDER = ['action', 'risk', 'waiting', 'active', 'paused', 'draft', 'done'];

export const BUCKET_HEADINGS = {
  action: 'Needs your decision',
  risk: 'At risk',
  waiting: 'Waiting on others',
  active: 'Active',
  paused: 'Paused',
  draft: 'Drafts',
  done: 'Closed',
};
